import { useTranslation } from 'react-i18next'
import { BaseText, BaseView } from '@/components/base'
import { ArcProgressBarProps } from './types'
import { truncateToDecimals } from '@/utils/currency/truncateToDecimals'
import { useTheme } from '@/hooks/useTheme'
import { responsiveAdapter, responsiveAdapterVertical } from '@/utils'

export const ArcTextContent = ({
  size,
  value,
  displayCurrencyValue,
  totalEarned,
  percentage,
  remainingToGoal,
  isLoading = true,
}: Omit<ArcProgressBarProps, 'size'> & {
  size: number
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const halfSize = size / 2

  const totalSavedDescription = totalEarned
    ? `${t('translation:goal.totalEarned')} ${totalEarned}`
    : ''

  const isGoalAchieved = percentage >= 1

  // Format value like in React Native - truncate to 2 decimal places without rounding
  const formatValue = (val: string) => {
    const numericValue = parseFloat(val)
    if (isNaN(numericValue)) return val

    // Truncate to 2 decimal places like in RN project
    const truncatedValue = truncateToDecimals({
      value: numericValue,
      decimals: 2,
    })

    // Format with up to 2 decimal places, removing trailing zeros
    return truncatedValue.toFixed(2).replace(/\.?0+$/, '')
  }

  const SkeletonBox: React.FC<{
    width: number
    height: number
    borderRadius?: string | number
    style?: React.CSSProperties
  }> = ({ width, height, borderRadius = '4px', style }) => (
    <BaseView
      style={{
        width: responsiveAdapter(width),
        height: responsiveAdapterVertical(height),
        backgroundColor:
          theme.colors.skeleton?.backgroundColor ||
          theme.colors.background.secondaryGray + '40',
        borderRadius,
        position: 'relative',
        overflow: 'hidden',
        ...style,
      }}
    >
      <BaseView
        style={{
          position: 'absolute',
          top: 0,
          left: '-100%',
          width: '100%',
          height: '100%',
          background: `linear-gradient(90deg, transparent, ${theme.colors.skeleton?.foregroundColor || 'rgba(255,255,255,0.1)'}, transparent)`,
          animation: 'skeleton-shimmer 1.5s infinite',
        }}
      />
    </BaseView>
  )

  return (
    <BaseView
      gap={8}
      center
      style={{
        position: 'absolute',
        top: 26,
        width: size,
        height: halfSize,
      }}
    >
      <style>
        {`
          @keyframes skeleton-shimmer {
            0% { left: -100%; }
            100% { left: 100%; }
          }
        `}
      </style>
      <BaseView center>
        <BaseText
          fontFamily="semibold"
          fontSize={30}
          style={{
            textAlign: 'center',
          }}
        >
          {isLoading ? (
            <SkeletonBox width={80} height={35} />
          ) : (
            formatValue(value)
          )}
        </BaseText>
        {displayCurrencyValue && (
          <BaseText
            fontSize={15}
            color="label.description"
            style={{
              textAlign: 'center',
            }}
          >
            ≈
            {isLoading ? (
              <SkeletonBox width={80} height={24} />
            ) : (
              displayCurrencyValue
            )}
          </BaseText>
        )}
      </BaseView>

      <BaseView center>
        {totalSavedDescription && (
          <BaseText
            fontSize={14}
            fontFamily="bold"
            style={{
              textAlign: 'center',
            }}
          >
            {isLoading ? (
              <SkeletonBox width={140} height={14} />
            ) : (
              totalSavedDescription
            )}
          </BaseText>
        )}

        <BaseText
          fontSize={14}
          color="label.description"
          style={{
            textAlign: 'center',
          }}
        >
          {isLoading ? (
            <SkeletonBox width={100} height={14} style={{ marginTop: 4 }} />
          ) : isGoalAchieved ? (
            t('translation:goal.goalAchieved')
          ) : (
            `${remainingToGoal} ${t('translation:goal.toGoal')}`
          )}
        </BaseText>
      </BaseView>
    </BaseView>
  )
}
